/**
 * FeedSyncPanel
 *
 * Allows Content Managers to restore product feed values for selected attributes.
 *
 * Features:
 * - Select category and attribute
 * - Choose reset mode (all values or null only)
 * - Option to preserve AI-researched and manually corrected values
 * - Preview affected products before execution
 * - Execute sync with progress tracking
 *
 * Date: 2026-01-30
 */

import React, { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Database,
  FileText,
  ArrowRight,
  Info,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';

// Types
interface CategoryOption {
  category: string;
  product_count: number;
}

interface AttributeOption {
  attribute_name: string;
  product_count: number;
  populated_percentage?: number;
}

interface FeedMetadata {
  source: 'live_feed' | 'cache';
  feedConfig: string;
  totalProducts?: number;
  feedCategories?: number;
  cachedAt?: string;
}

interface PreviewProduct {
  id: string;
  name: string;
  feedValue: string;
  normalizedValue?: string | null;
  currentValue: string | null;
  currentOrigin: string | null;
}

interface PreviewResult {
  totalWithFeedValue: number;
  wouldUpdate: number;
  wouldSkip: {
    aiResearched: number;
    manual: number;
    hasValue: number;
  };
  sampleProducts: PreviewProduct[];
  normalizationApplied?: {
    enabled: boolean;
    applicable: boolean;
    fieldType: string | null;
  };
}

interface NormalizationInfo {
  applicable: boolean;
  fieldType?: string;
  inferredUnit?: string;
  confidence?: number;
  baseUnit?: string;
  multiplier?: number;
  reason?: string;
  sampleConversions?: Array<{
    original: string;
    normalized: string;
    numericValue: number;
    unit: string;
    count: number;
  }>;
}

interface AttributeInfo {
  normalization: NormalizationInfo;
  existingStats?: {
    count: number;
    min: number;
    max: number;
    mean: number;
    median: number;
    expectedRange: { min: number; max: number };
  };
  rangeValidation?: {
    normalizedRange: { min: number; max: number };
    existingRange: { min: number; max: number };
    inRange: boolean;
    warning: string | null;
  };
}

interface ExecuteResult {
  totalProcessed: number;
  updated: number;
  skipped: number;
  errors: number;
  errorDetails: Array<{
    productId: string;
    productName: string;
    error: string;
  }>;
}

export function FeedSyncPanel() {
  const { selectedSite } = useSite();
  const { toast } = useToast();

  // Form state
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedAttribute, setSelectedAttribute] = useState<string>('');
  const [resetMode, setResetMode] = useState<'all' | 'null_only'>('null_only');
  const [keepAiResearched, setKeepAiResearched] = useState(true);
  const [keepManual, setKeepManual] = useState(true);
  const [normalizeValues, setNormalizeValues] = useState(false);

  // Data state
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [attributes, setAttributes] = useState<AttributeOption[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);
  const [feedMetadata, setFeedMetadata] = useState<FeedMetadata | null>(null);
  const [attributeLoadError, setAttributeLoadError] = useState<string | null>(null);
  const [attributeInfo, setAttributeInfo] = useState<AttributeInfo | null>(null);
  const [isLoadingAttributeInfo, setIsLoadingAttributeInfo] = useState(false);

  // Preview state
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  // Execute state
  const [isExecuting, setIsExecuting] = useState(false);
  const [executeResult, setExecuteResult] = useState<ExecuteResult | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Load categories on mount
  useEffect(() => {
    if (selectedSite?.id) {
      loadCategories();
    }
  }, [selectedSite?.id]);

  // Load attributes when category changes
  useEffect(() => {
    if (selectedSite?.id) {
      loadAttributes();
      setSelectedAttribute('');
      setPreview(null);
    }
  }, [selectedSite?.id, selectedCategory]);

  // Clear preview when options change
  useEffect(() => {
    setPreview(null);
    setExecuteResult(null);
  }, [selectedAttribute, resetMode, keepAiResearched, keepManual, normalizeValues]);

  // Fetch attribute info when attribute is selected (for normalization detection)
  useEffect(() => {
    if (selectedSite?.id && selectedAttribute) {
      loadAttributeInfo();
    } else {
      setAttributeInfo(null);
      setNormalizeValues(false);
    }
  }, [selectedSite?.id, selectedAttribute]);

  const loadCategories = async () => {
    if (!selectedSite?.id) return;

    setIsLoadingCategories(true);
    try {
      const response = await authFetch(
        `/api/product-enrichment/feed-sync/categories?site_id=${selectedSite.id}`
      );

      if (!response.ok) {
        throw new Error('Failed to load categories');
      }

      const data = await response.json();
      if (data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      toast({
        title: 'Error',
        description: 'Failed to load categories',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const loadAttributes = async (refresh = false) => {
    if (!selectedSite?.id) return;

    setIsLoadingAttributes(true);
    setAttributeLoadError(null);
    try {
      const params = new URLSearchParams({
        site_id: selectedSite.id,
      });
      if (selectedCategory !== 'all') {
        params.append('category', selectedCategory);
      }
      if (refresh) {
        params.append('refresh', 'true');
      }

      const response = await authFetch(
        `/api/product-enrichment/feed-sync/attributes?${params.toString()}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to load attributes from feed');
      }

      const data = await response.json();
      if (data.success) {
        setAttributes(data.attributes || []);
        setFeedMetadata({
          source: data.source || 'live_feed',
          feedConfig: data.feed_config || 'Unknown',
          totalProducts: data.total_products,
          feedCategories: data.feed_categories,
          cachedAt: data.cached_at,
        });
      } else {
        throw new Error(data.error || 'Failed to load attributes');
      }
    } catch (error) {
      console.error('Error loading attributes:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to load attributes from feed';
      setAttributeLoadError(errorMessage);
      toast({
        title: 'Error Loading Feed',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsLoadingAttributes(false);
    }
  };

  const refreshFeedAttributes = () => {
    loadAttributes(true);
  };

  const loadAttributeInfo = async () => {
    if (!selectedSite?.id || !selectedAttribute) return;

    setIsLoadingAttributeInfo(true);
    try {
      const response = await authFetch('/api/product-enrichment/feed-sync/attribute-info', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSite.id,
          attribute: selectedAttribute,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to load attribute info');
      }

      const data = await response.json();
      if (data.success) {
        setAttributeInfo({
          normalization: data.normalization,
          existingStats: data.existingStats,
          rangeValidation: data.rangeValidation,
        });
        // Auto-enable normalization if applicable and high confidence
        if (data.normalization?.applicable && data.normalization?.confidence >= 0.9) {
          setNormalizeValues(true);
        } else {
          setNormalizeValues(false);
        }
      }
    } catch (error) {
      console.error('Error loading attribute info:', error);
      setAttributeInfo(null);
    } finally {
      setIsLoadingAttributeInfo(false);
    }
  };

  const runPreview = async () => {
    if (!selectedSite?.id || !selectedAttribute) return;

    setIsPreviewing(true);
    setPreview(null);
    setExecuteResult(null);

    try {
      const response = await authFetch('/api/product-enrichment/feed-sync/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSite.id,
          category: selectedCategory,
          attribute: selectedAttribute,
          reset_mode: resetMode,
          keep_ai_researched: keepAiResearched,
          keep_manual: keepManual,
          normalize: normalizeValues && attributeInfo?.normalization?.applicable,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to preview sync');
      }

      const data = await response.json();
      if (data.success) {
        setPreview(data.preview);
      } else {
        throw new Error(data.error || 'Preview failed');
      }
    } catch (error) {
      console.error('Error previewing sync:', error);
      toast({
        title: 'Preview Error',
        description: error instanceof Error ? error.message : 'Failed to preview sync',
        variant: 'destructive',
      });
    } finally {
      setIsPreviewing(false);
    }
  };

  const executeSync = async () => {
    if (!selectedSite?.id || !selectedAttribute) return;

    setIsExecuting(true);
    setShowConfirmDialog(false);

    try {
      const response = await authFetch('/api/product-enrichment/feed-sync/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_id: selectedSite.id,
          category: selectedCategory,
          attribute: selectedAttribute,
          reset_mode: resetMode,
          keep_ai_researched: keepAiResearched,
          keep_manual: keepManual,
          normalize: normalizeValues && attributeInfo?.normalization?.applicable,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to execute sync');
      }

      const data = await response.json();
      if (data.success) {
        setExecuteResult(data.result);
        toast({
          title: 'Sync Complete',
          description: `Updated ${data.result.updated} products`,
        });
      } else {
        throw new Error(data.error || 'Sync failed');
      }
    } catch (error) {
      console.error('Error executing sync:', error);
      toast({
        title: 'Sync Error',
        description: error instanceof Error ? error.message : 'Failed to execute sync',
        variant: 'destructive',
      });
    } finally {
      setIsExecuting(false);
    }
  };

  const formatOriginBadge = (origin: string | null) => {
    if (!origin) return <Badge variant="secondary">Unknown</Badge>;

    const variants: Record<string, { color: string; label: string }> = {
      feed: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', label: 'Feed' },
      ai_extracted: { color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300', label: 'AI Extracted' },
      ai_researched: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', label: 'AI Researched' },
      manual: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', label: 'Manual' },
    };

    const variant = variants[origin] || { color: 'bg-gray-100 text-gray-800', label: origin };
    return <Badge className={variant.color}>{variant.label}</Badge>;
  };

  if (!selectedSite) {
    return (
      <Card>
        <CardContent className="py-8">
          <p className="text-center text-muted-foreground">
            Please select a site to use Feed Sync
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Description Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Feed Sync
          </CardTitle>
          <CardDescription>
            Sync product attributes from the <strong>live feed URL</strong>. This includes:
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-0">
          <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
            <li>New fields added to the feed since initial import</li>
            <li>Fields that were originally excluded during import</li>
            <li>Updated values in the current feed</li>
          </ul>
        </CardContent>
      </Card>

      {/* Configuration Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Configuration</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Category Selection */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
                disabled={isLoadingCategories}
              >
                <SelectTrigger id="category">
                  <SelectValue placeholder="Select category..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category} value={cat.category}>
                      {cat.category} ({cat.product_count})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isLoadingCategories && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Loading categories...
                </div>
              )}
            </div>

            {/* Attribute Selection */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="attribute">Attribute (from live feed)</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={refreshFeedAttributes}
                  disabled={isLoadingAttributes}
                  className="h-6 px-2 text-xs"
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${isLoadingAttributes ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
              <Select
                value={selectedAttribute}
                onValueChange={setSelectedAttribute}
                disabled={isLoadingAttributes || attributes.length === 0}
              >
                <SelectTrigger id="attribute">
                  <SelectValue placeholder={isLoadingAttributes ? "Loading from feed..." : "Select attribute..."} />
                </SelectTrigger>
                <SelectContent>
                  {attributes.map((attr) => (
                    <SelectItem key={attr.attribute_name} value={attr.attribute_name}>
                      {attr.attribute_name} ({attr.product_count} products{attr.populated_percentage ? `, ${attr.populated_percentage}%` : ''})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isLoadingAttributes && (
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Downloading and analyzing live feed...
                </div>
              )}
              {attributeLoadError && (
                <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                  <AlertTriangle className="h-3 w-3" />
                  {attributeLoadError}
                </div>
              )}
              {feedMetadata && !isLoadingAttributes && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Info className="h-3 w-3" />
                  {feedMetadata.source === 'cache' ? (
                    <span>Cached from "{feedMetadata.feedConfig}" ({feedMetadata.cachedAt ? new Date(feedMetadata.cachedAt).toLocaleTimeString() : 'recent'})</span>
                  ) : (
                    <span>Live from "{feedMetadata.feedConfig}" ({feedMetadata.totalProducts?.toLocaleString()} products)</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Reset Mode */}
          <div className="space-y-3">
            <Label>Reset Mode</Label>
            <RadioGroup
              value={resetMode}
              onValueChange={(v) => setResetMode(v as 'all' | 'null_only')}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="null_only" id="null_only" />
                <Label htmlFor="null_only" className="font-normal cursor-pointer">
                  <span className="font-medium">Only fill empty values</span>
                  <span className="text-muted-foreground ml-2">
                    Only update products where the attribute is currently null/empty
                  </span>
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <RadioGroupItem value="all" id="all" />
                <Label htmlFor="all" className="font-normal cursor-pointer">
                  <span className="font-medium">Reset all values</span>
                  <span className="text-muted-foreground ml-2">
                    Overwrite all existing values with feed data
                  </span>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Preservation Options */}
          <div className="space-y-3">
            <Label>Preserve Values</Label>
            <div className="flex flex-col gap-3">
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="keep_ai_researched"
                  checked={keepAiResearched}
                  onCheckedChange={(checked) => setKeepAiResearched(checked === true)}
                />
                <Label htmlFor="keep_ai_researched" className="font-normal cursor-pointer">
                  Keep AI-researched values
                  <span className="text-muted-foreground ml-2">
                    Don't overwrite values that were researched by AI
                  </span>
                </Label>
              </div>
              <div className="flex items-center space-x-3">
                <Checkbox
                  id="keep_manual"
                  checked={keepManual}
                  onCheckedChange={(checked) => setKeepManual(checked === true)}
                />
                <Label htmlFor="keep_manual" className="font-normal cursor-pointer">
                  Keep manually corrected values
                  <span className="text-muted-foreground ml-2">
                    Don't overwrite values that were manually entered
                  </span>
                </Label>
              </div>
            </div>
          </div>

          {/* Unit Normalization (shown when applicable) */}
          {selectedAttribute && (
            <div className="space-y-3">
              <Label>Unit Normalization</Label>
              {isLoadingAttributeInfo ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Analyzing attribute values...
                </div>
              ) : attributeInfo?.normalization?.applicable ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="normalize_values"
                      checked={normalizeValues}
                      onCheckedChange={(checked) => setNormalizeValues(checked === true)}
                    />
                    <Label htmlFor="normalize_values" className="font-normal cursor-pointer">
                      <span className="font-medium">Normalize units</span>
                      <span className="text-muted-foreground ml-2">
                        Convert {attributeInfo.normalization.inferredUnit} → {attributeInfo.normalization.baseUnit}
                        {attributeInfo.normalization.confidence && (
                          <span className="text-xs ml-1">
                            ({Math.round(attributeInfo.normalization.confidence * 100)}% confidence)
                          </span>
                        )}
                      </span>
                    </Label>
                  </div>

                  {/* Sample conversions */}
                  {attributeInfo.normalization.sampleConversions && attributeInfo.normalization.sampleConversions.length > 0 && (
                    <div className="ml-6 p-3 rounded-md bg-muted/50 space-y-2">
                      <div className="text-xs font-medium text-muted-foreground">Sample conversions:</div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        {attributeInfo.normalization.sampleConversions.slice(0, 5).map((conv, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-muted-foreground">{conv.original}</span>
                            <ArrowRight className="h-3 w-3 text-muted-foreground" />
                            <span className="font-medium text-green-600 dark:text-green-400">{conv.normalized}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Range validation warning */}
                  {attributeInfo.rangeValidation && !attributeInfo.rangeValidation.inRange && (
                    <Alert variant="destructive" className="ml-6">
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription className="text-sm">
                        {attributeInfo.rangeValidation.warning}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Existing database stats */}
                  {attributeInfo.existingStats && (
                    <div className="ml-6 text-xs text-muted-foreground">
                      Existing values in database: {attributeInfo.existingStats.min} - {attributeInfo.existingStats.max} {attributeInfo.normalization.baseUnit}
                      (mean: {attributeInfo.existingStats.mean}, n={attributeInfo.existingStats.count})
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No unit normalization needed for this attribute
                  {attributeInfo?.normalization?.reason && (
                    <span className="text-xs ml-2">({attributeInfo.normalization.reason})</span>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Preview Button */}
          <div className="pt-4 border-t">
            <Button
              onClick={runPreview}
              disabled={!selectedAttribute || isPreviewing}
              className="w-full md:w-auto"
            >
              {isPreviewing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <FileText className="mr-2 h-4 w-4" />
                  Preview Changes
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview Results */}
      {preview && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="h-5 w-5" />
              Preview Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Summary Stats */}
            <div className="grid gap-4 md:grid-cols-4">
              <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
                <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                  {preview.totalWithFeedValue}
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400">
                  Products with feed value
                </div>
              </div>
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                  {preview.wouldUpdate}
                </div>
                <div className="text-sm text-green-600 dark:text-green-400">
                  Would be updated
                </div>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                  {preview.wouldSkip.aiResearched + preview.wouldSkip.manual}
                </div>
                <div className="text-sm text-amber-600 dark:text-amber-400">
                  Would be preserved
                </div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-950/30 border border-gray-200 dark:border-gray-800">
                <div className="text-2xl font-bold text-gray-700 dark:text-gray-300">
                  {preview.wouldSkip.hasValue}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Already have values
                </div>
              </div>
            </div>

            {/* Skip Breakdown */}
            {(preview.wouldSkip.aiResearched > 0 || preview.wouldSkip.manual > 0) && (
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <div className="flex flex-wrap gap-4 mt-1">
                    {preview.wouldSkip.aiResearched > 0 && (
                      <span>
                        <strong>{preview.wouldSkip.aiResearched}</strong> AI-researched values preserved
                      </span>
                    )}
                    {preview.wouldSkip.manual > 0 && (
                      <span>
                        <strong>{preview.wouldSkip.manual}</strong> manually corrected values preserved
                      </span>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            {/* Normalization info */}
            {preview.normalizationApplied?.enabled && preview.normalizationApplied?.applicable && (
              <Alert className="bg-green-50 dark:bg-green-950/30 border-green-200 dark:border-green-800">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700 dark:text-green-300">
                  Unit normalization will be applied to values
                </AlertDescription>
              </Alert>
            )}

            {/* Sample Products Table */}
            {preview.sampleProducts.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium">Sample Products</h4>
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>Current Value</TableHead>
                        <TableHead>Origin</TableHead>
                        <TableHead className="text-center">
                          <ArrowRight className="h-4 w-4 inline" />
                        </TableHead>
                        <TableHead>Feed Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {preview.sampleProducts.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium max-w-[200px] truncate">
                            {product.name}
                          </TableCell>
                          <TableCell className="text-muted-foreground">
                            {product.currentValue || <span className="italic">null</span>}
                          </TableCell>
                          <TableCell>
                            {formatOriginBadge(product.currentOrigin)}
                          </TableCell>
                          <TableCell className="text-center text-muted-foreground">
                            <ArrowRight className="h-4 w-4 inline" />
                          </TableCell>
                          <TableCell className="font-medium">
                            {product.normalizedValue ? (
                              <div className="flex flex-col">
                                <span className="text-green-600 dark:text-green-400">
                                  {product.normalizedValue}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  (from: {product.feedValue})
                                </span>
                              </div>
                            ) : (
                              <span className="text-blue-600 dark:text-blue-400">
                                {product.feedValue}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {preview.totalWithFeedValue > 10 && (
                  <p className="text-sm text-muted-foreground">
                    Showing first 10 of {preview.totalWithFeedValue} products
                  </p>
                )}
              </div>
            )}

            {/* Execute Button */}
            {preview.wouldUpdate > 0 && (
              <div className="pt-4 border-t flex justify-end">
                <Button
                  onClick={() => setShowConfirmDialog(true)}
                  disabled={isExecuting}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Sync {preview.wouldUpdate} Products
                </Button>
              </div>
            )}

            {preview.wouldUpdate === 0 && (
              <Alert>
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  No products would be updated with the current settings.
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Execute Results */}
      {executeResult && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              Sync Complete
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                  {executeResult.updated}
                </div>
                <div className="text-sm text-green-600 dark:text-green-400">
                  Products updated
                </div>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <div className="text-2xl font-bold text-amber-700 dark:text-amber-300">
                  {executeResult.skipped}
                </div>
                <div className="text-sm text-amber-600 dark:text-amber-400">
                  Products skipped
                </div>
              </div>
              {executeResult.errors > 0 && (
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800">
                  <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                    {executeResult.errors}
                  </div>
                  <div className="text-sm text-red-600 dark:text-red-400">
                    Errors
                  </div>
                </div>
              )}
            </div>

            {executeResult.errorDetails.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <div className="mt-2 space-y-1">
                    {executeResult.errorDetails.map((err, idx) => (
                      <div key={idx} className="text-sm">
                        <strong>{err.productName}:</strong> {err.error}
                      </div>
                    ))}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Feed Sync</DialogTitle>
            <DialogDescription>
              This will update <strong>{preview?.wouldUpdate || 0} products</strong> with
              the feed value for <strong>{selectedAttribute}</strong>.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-2">
            <p className="text-sm text-muted-foreground">
              <strong>Category:</strong> {selectedCategory === 'all' ? 'All Categories' : selectedCategory}
            </p>
            <p className="text-sm text-muted-foreground">
              <strong>Mode:</strong> {resetMode === 'all' ? 'Reset all values' : 'Only fill empty values'}
            </p>
            {keepAiResearched && (
              <p className="text-sm text-green-600">
                AI-researched values will be preserved
              </p>
            )}
            {keepManual && (
              <p className="text-sm text-orange-600">
                Manually corrected values will be preserved
              </p>
            )}
            {normalizeValues && attributeInfo?.normalization?.applicable && (
              <p className="text-sm text-blue-600">
                Unit normalization: {attributeInfo.normalization.inferredUnit} → {attributeInfo.normalization.baseUnit}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button
              onClick={executeSync}
              disabled={isExecuting}
              className="bg-green-600 hover:bg-green-700"
            >
              {isExecuting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Syncing...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Confirm Sync
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
